(ns app.bulk.gallery-modal
  "Modal for selecting story media from the gallery to add to bulk schedule."
  (:require [parts.replicant.query :as query]
            [storrito.date.datetime-local :as datetime-local]))

(def gallery-query-base
  {:query/kind :query/bulk-gallery
   :query/data {:offset 0}})

(defn- gallery-query
  [state]
  (let [offset (or (::offset state) 0)
        folder (::folder-uuid state)]
    (cond-> {:query/kind :query/bulk-gallery
             :query/data {:offset offset}}
      folder (assoc-in [:query/data :folder-uuid] folder))))

;; -- Selection --

(defn- select-entries
  [state entries]
  (reduce (fn [s entry]
            (let [eid (:story-media/eid entry)]
              (if (contains? (::selected-eids s) eid)
                s
                (-> s
                    (update ::selected-eids (fnil conj #{}) eid)
                    (update ::selected-entries (fnil conj []) entry)))))
          state
          entries))

(defn- deselect-entries
  [state entries]
  (let [eids (into #{}
                   (map :story-media/eid)
                   entries)]
    (-> state
        (update ::selected-eids
                (fn [selected]
                  (apply disj (or selected #{}) eids)))
        (update ::selected-entries
                (fn [selected-entries]
                  (vec (remove (comp eids :story-media/eid)
                               selected-entries)))))))

(defn item-click
  "Handles a click on a gallery item of the 'Add from gallery' modal.
   A plain click toggles the entry and remembers it as the anchor for a
   subsequent shift-click, which applies the clicked entry's toggle to
   all entries between the anchor and the clicked entry (like in the
   main gallery): shift-clicking an unselected entry selects the range,
   shift-clicking a selected entry deselects it. The range spans the
   current page only — the modal is paginated and the selection needs
   the full entry maps — so a shift-click whose anchor is not on the
   current page (after pagination or a folder change) falls back to a
   plain toggle."
  [{:keys [store action] :as w}]
  (let [[_ entry] action
        eid (:story-media/eid entry)
        state @store
        entries (vec (:gallery/entries
                       (query/get-result state
                                         (gallery-query state))))
        index-of (fn [wanted-eid]
                   (first (keep-indexed
                            (fn [idx e]
                              (when (= (:story-media/eid e) wanted-eid)
                                idx))
                            entries)))
        anchor-idx (when (and (.-shiftKey (:replicant/js-event w))
                              (::range-anchor state))
                     (index-of (::range-anchor state)))
        click-idx (when anchor-idx
                    (index-of eid))]
    (if (and anchor-idx click-idx)
      ;; the anchor stays unchanged, so consecutive shift-clicks
      ;; extend from the same anchor:
      (let [[from-idx to-idx] (sort [anchor-idx click-idx])
            range-entries (subvec entries from-idx (inc to-idx))
            deselect? (contains? (::selected-eids state) eid)]
        (swap! store
               (fn [s]
                 (if deselect?
                   (deselect-entries s range-entries)
                   (select-entries s range-entries)))))
      (swap! store
             (fn [s]
               (-> (if (contains? (::selected-eids s) eid)
                     (deselect-entries s [entry])
                     (select-entries s [entry]))
                   (assoc ::range-anchor eid)))))))

;; -- Gallery grid --

(def gallery-card-width 90)
(def gallery-card-gap 8)

(defn- entry-name-overlay
  "Gradient overlay with the file name at the bottom of a gallery
   preview."
  [entry]
  [:div {:class ["absolute" "bottom-0" "left-0" "right-0" "px-1" "py-0.5"
                 "bg-gradient-to-t" "from-black/60" "to-transparent"]}
   [:span {:class ["text-white" "text-xs" "truncate" "block"]}
    (or (:story-media/name entry) "")]])

(defn- gallery-item
  [entry selected-eids]
  (let [eid (:story-media/eid entry)
        is-selected (contains? selected-eids eid)]
    [:div
     {:class (into ["relative" "cursor-pointer" "rounded-lg" "overflow-hidden"
                    "border-2" "transition-all"]
                   (if is-selected
                     ["border-indigo-500" "ring-2" "ring-indigo-300"]
                     ["border-transparent" "hover:border-gray-300"]))
      :replicant/key eid
      :on {:click [[:bulk-gallery/item-click entry]]}}
     [:div {:style {:aspect-ratio "9/16"}}
      (if (:story-media/preview-url entry)
        [:img {:src (:story-media/preview-url entry)
               :class ["w-full" "h-full" "object-cover"]}]
        [:div {:class ["w-full" "h-full" "bg-gray-100" "flex" "items-center"
                       "justify-center" "text-gray-400" "text-xs"]}
         "No preview"])]
     (when is-selected
       [:div {:class ["absolute" "top-1" "right-1" "w-5" "h-5" "rounded-full"
                      "bg-indigo-500" "flex" "items-center" "justify-center"]}
        [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
               :fill "white" :class "w-3 h-3"}
         [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                 :d "M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"}]]])
     (entry-name-overlay entry)]))

(defn- folder-breadcrumb
  [state folder-tree]
  (when folder-tree
    (let [path (:folder-path folder-tree)]
      [:div {:class ["text-sm" "text-gray-500" "mb-2"]}
       (when (::folder-uuid state)
         [:div {:class ["flex" "items-center" "gap-1" "overflow-x-auto" "pb-1"]}
          [:button
           {:class ["text-indigo-600" "hover:text-indigo-700" "cursor-pointer"
                    "shrink-0"]
            :on {:click [[:store/assoc ::folder-uuid nil]
                         [:store/assoc ::offset 0]
                         [:data/query (gallery-query
                                        (assoc state
                                               ::folder-uuid nil
                                               ::offset 0))]]}}
           "All"]
          (when (and path (seq path))
            (mapcat (fn [folder]
                      [[:span {:class ["mx-1"]} "/"]
                       [:span {:class ["shrink-0"]}
                        (:story-media-folder/name folder)]])
                    path))])
       (when-let [children (seq (:children folder-tree))]
         [:div {:class ["flex" "flex-wrap" "gap-1.5" "mt-1"]}
          (map (fn [child]
                 [:button
                  {:class ["px-2.5" "py-1" "text-xs" "bg-gray-100" "rounded-md"
                           "hover:bg-gray-200" "cursor-pointer" "shrink-0"]
                   :replicant/key (:uuid child)
                   :on {:click [[:store/assoc ::folder-uuid (:uuid child)]
                                [:store/assoc ::offset 0]
                                [:data/query (gallery-query
                                               (assoc state
                                                      ::folder-uuid (:uuid child)
                                                      ::offset 0))]]}}
                  (:name child)])
               children)])])))

(defn- pagination
  [state total-count offset]
  (let [page-size 100
        has-prev (pos? offset)
        has-next (< (+ offset page-size) total-count)]
    (when (or has-prev has-next)
      [:div {:class ["flex" "items-center" "justify-between" "text-sm"
                     "text-gray-500"]}
       [:button
        {:class (into ["px-3" "py-1" "rounded" "border" "text-sm"]
                      (if has-prev
                        ["border-gray-300" "hover:bg-gray-50" "cursor-pointer"]
                        ["border-gray-200" "text-gray-300" "cursor-not-allowed"]))
         :disabled (not has-prev)
         :on {:click (when has-prev
                       (let [new-offset (max 0 (- offset page-size))]
                         [[:store/assoc ::offset new-offset]
                          [:data/query (gallery-query
                                         (assoc state ::offset new-offset))]]))}}
        "Previous"]
       [:span (str (inc offset) " - " (min (+ offset page-size) total-count)
                   " of " total-count)]
       [:button
        {:class (into ["px-3" "py-1" "rounded" "border" "text-sm"]
                      (if has-next
                        ["border-gray-300" "hover:bg-gray-50" "cursor-pointer"]
                        ["border-gray-200" "text-gray-300" "cursor-not-allowed"]))
         :disabled (not has-next)
         :on {:click (when has-next
                       (let [new-offset (+ offset page-size)]
                         [[:store/assoc ::offset new-offset]
                          [:data/query (gallery-query
                                         (assoc state ::offset new-offset))]]))}}
        "Next"]])))

;; -- Selected items strip --

(defn- selected-strip
  [state]
  (let [entries (::selected-entries state)
        count (count entries)]
    (when (pos? count)
      [:div {:class ["border-t" "border-gray-200" "pt-3"]}
       [:div {:class ["text-sm" "font-medium" "text-gray-700" "mb-2"]}
        (str "Selected (" count ")")]
       [:div {:class ["flex" "gap-2" "overflow-x-auto" "pb-1"]}
        (map (fn [entry]
               [:div {:class ["relative" "shrink-0" "w-16" "rounded"
                              "overflow-hidden"]
                      :replicant/key (:story-media/eid entry)
                      :style {:aspect-ratio "9/16"}}
                (when (:story-media/preview-url entry)
                  [:img {:src (:story-media/preview-url entry)
                         :class ["w-full" "h-full" "object-cover"]}])
                (entry-name-overlay entry)
                [:button
                 {:class ["absolute" "top-0" "right-0" "w-4" "h-4" "bg-black/50"
                          "text-white" "text-xs" "flex" "items-center"
                          "justify-center" "cursor-pointer"]
                  :on {:click [[:bulk-gallery/deselect-item (:story-media/eid entry)]]}}
                 "×"]])
             entries)]])))

;; -- Main render --

(defn render
  [state]
  (let [q (gallery-query state)
        result (query/get-result state q)
        entries (:gallery/entries result)
        total-count (or (:gallery/total-count result) 0)
        offset (or (:gallery/offset result) 0)
        folder-tree (:gallery/folder-tree result)
        selected-eids (or (::selected-eids state) #{})
        schedule-date-str (or (::schedule-date state)
                              (datetime-local/next-10-minute-datetime-local))
        schedule-date-valid? (datetime-local/current-minute-or-future?
                               (datetime-local/date-ms schedule-date-str))
        loading (and (query/loading? state q)
                     (not (query/available? state q)))]
    [:div {:class ["flex" "flex-col" "gap-3" "min-h-0"]
           :style {:max-height "90vh"}}
     [:h3 {:class ["text-lg" "font-semibold" "text-gray-900"]}
      "Add from gallery"]

     (folder-breadcrumb state folder-tree)

     (if loading
       [:div {:class ["flex" "items-center" "justify-center" "py-12"]}
        [:div {:class ["text-gray-400"]} "Loading..."]]
       (if (empty? entries)
         [:div {:class ["text-center" "py-12" "text-gray-400"]}
          "Your gallery is empty."]
         [:div {:class ["overflow-y-auto" "flex-1" "min-h-0"]}
          [:div {:style {:display "grid"
                         :grid-template-columns (str "repeat(auto-fill, "
                                                       gallery-card-width
                                                       "px)")
                         :gap (str gallery-card-gap "px")
                         :justify-content "center"}}
           (map (fn [entry]
                  (gallery-item entry selected-eids))
                entries)]]))

     (pagination state total-count offset)

     (selected-strip state)

     ;; Schedule date + add button
     [:div {:class ["border-t" "border-gray-200" "pt-3" "flex" "items-end"
                    "gap-3" "flex-wrap"]}
      [:div {:class ["flex-1" "min-w-[12rem]"]}
       [:label {:class ["text-sm" "font-medium" "text-gray-700" "mb-1" "block"]}
        "Start date"]
       [:input
        {:type "datetime-local"
         :value schedule-date-str
         :min (datetime-local/current-datetime-local)
         :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                 "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                 "focus:border-indigo-500"]
         :on {:input [[:store/assoc ::schedule-date :event/target.value]]}}]
       (when-not schedule-date-valid?
         [:div {:class ["mt-1" "text-xs" "text-red-600"]}
          "Start date must be in the future"])]
      [:button
       {:class (into ["px-4" "py-2" "text-sm" "font-semibold" "rounded-lg"
                      "cursor-pointer"]
                     (if (and (seq selected-eids) schedule-date-valid?)
                       ["bg-indigo-600" "text-white" "hover:bg-indigo-700"]
                       ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
        :disabled (or (empty? selected-eids)
                      (not schedule-date-valid?))
        :on {:click (when (and (seq selected-eids) schedule-date-valid?)
                      [[:bulk/add-selected-to-schedule
                        {:entries (::selected-entries state)
                         :date-str schedule-date-str}]
                       [:ui/modal-close]])}}
       (str "Add " (count selected-eids) " item"
            (when (not= 1 (count selected-eids)) "s"))]
      [:button
       {:class ["px-4" "py-2" "text-sm" "font-medium" "rounded-lg"
                "border" "border-gray-300" "text-gray-700" "bg-white"
                "hover:bg-gray-50" "cursor-pointer"]
        :on {:click [[:ui/modal-close]]}}
       "Cancel"]]]))

(def modal
  {:modal-id :modals/bulk-gallery
   :on-load (fn [_]
              [[:store/assoc ::selected-eids #{}]
               [:store/assoc ::selected-entries []]
               [:store/assoc ::range-anchor nil]
               [:store/assoc ::offset 0]
               [:store/assoc ::folder-uuid nil]
               [:store/assoc ::schedule-date nil]
               [:data/query gallery-query-base]])
   :render #'render})
